// src/feedback/feedbackDrawer.ts — Interactive Victorian Atelier Feedback Drawer UI

import { tokens } from '../ui/designTokens';
import { soundEngine } from '../audio/soundEngine';
import type {
  ChatMessage,
  FeedbackCategory,
  FeedbackEntry,
  FeedbackSeverity,
  TelemetrySnapshot,
} from './feedbackTypes';
import { FeedbackAgentClient } from './feedbackAgentClient';
import { FeedbackStorage } from './feedbackStorage';

export interface FeedbackDrawerOptions {
  onClose?: () => void;
  onEntrySaved?: (entry: FeedbackEntry) => void;
}

export class FeedbackDrawer {
  private container: HTMLElement;
  private isOpen: boolean = false;
  private isThinking: boolean = false;
  private messages: ChatMessage[] = [];
  private telemetry: TelemetrySnapshot;
  private activeCategory: FeedbackCategory = 'adhd_friction';
  private activeSeverity: FeedbackSeverity = 'medium';
  private suggestedTitle: string = 'Atelier Observation';
  private suggestedAction: string = '';
  private currentEntryId: string = '';
  private isSaved: boolean = false;
  private aiLive: boolean = false;
  private pendingImage: { dataUrl: string; name: string } | null = null;
  private quickChips: string[] = [
    'Spotted a glitch',
    'ADHD / Pacing note',
    'Question wording check',
    'Feature suggestion',
  ];
  private onClose?: () => void;
  private onEntrySaved?: (entry: FeedbackEntry) => void;

  constructor(options: FeedbackDrawerOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'feedback-drawer-root';
    this.container.className = 'feedback-drawer-wrapper';
    this.onClose = options.onClose;
    this.onEntrySaved = options.onEntrySaved;

    this.telemetry = FeedbackAgentClient.captureTelemetry();
    this.initSession();
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getIsOpen(): boolean {
    return this.isOpen;
  }

  getIsSaved(): boolean {
    return this.isSaved;
  }

  getPendingImage(): { dataUrl: string; name: string } | null {
    return this.pendingImage;
  }

  setPendingImage(img: { dataUrl: string; name: string } | null): void {
    this.pendingImage = img;
    if (this.isOpen) this.render();
  }

  handleImageFile(file: File): void {
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const rawDataUrl = e.target?.result as string;
      if (!rawDataUrl) return;

      try {
        const img = new Image();
        let handled = false;
        const fallbackTimer = setTimeout(() => {
          if (!handled) {
            handled = true;
            this.pendingImage = {
              dataUrl: rawDataUrl,
              name: file.name || 'Pasted screenshot',
            };
            this.render();
          }
        }, 300);

        img.onload = () => {
          if (handled) return;
          handled = true;
          clearTimeout(fallbackTimer);
          try {
            const maxDim = 1200;
            let w = img.width;
            let h = img.height;
            if (w > maxDim || h > maxDim) {
              if (w > h) {
                h = Math.round((h * maxDim) / w);
                w = maxDim;
              } else {
                w = Math.round((w * maxDim) / h);
                h = maxDim;
              }
            }
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0, w, h);
              this.pendingImage = {
                dataUrl: canvas.toDataURL('image/jpeg', 0.85),
                name: file.name || 'Pasted screenshot',
              };
            } else {
              this.pendingImage = {
                dataUrl: rawDataUrl,
                name: file.name || 'Pasted screenshot',
              };
            }
          } catch {
            this.pendingImage = {
              dataUrl: rawDataUrl,
              name: file.name || 'Pasted screenshot',
            };
          }
          this.render();
          soundEngine.playTap();
        };

        img.onerror = () => {
          if (handled) return;
          handled = true;
          clearTimeout(fallbackTimer);
          this.pendingImage = {
            dataUrl: rawDataUrl,
            name: file.name || 'Pasted screenshot',
          };
          this.render();
        };

        img.src = rawDataUrl;
      } catch {
        this.pendingImage = {
          dataUrl: rawDataUrl,
          name: file.name || 'Pasted screenshot',
        };
        this.render();
      }
    };
    reader.readAsDataURL(file);
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.telemetry = FeedbackAgentClient.captureTelemetry();
    this.checkAiStatus();
    this.render();
    soundEngine.playTap();

    setTimeout(() => {
      const input = this.container.querySelector<HTMLInputElement>('#feedback-chat-input');
      input?.focus();
    }, 150);
  }

  private async checkAiStatus(): Promise<void> {
    try {
      const res = await fetch('/api/ai/status');
      if (res.ok) {
        const data = await res.json();
        this.aiLive = Boolean(data.live);
        if (this.isOpen) this.render();
      }
    } catch {
      this.aiLive = false;
    }
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.render();
    this.onClose?.();
  }

  private initSession(): void {
    this.currentEntryId = `fb_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    this.isSaved = false;
    this.telemetry = FeedbackAgentClient.captureTelemetry();

    const routeName = this.telemetry.route === '#/' ? 'Expedition Map' : this.telemetry.route;

    this.messages = [
      {
        id: 'msg_0',
        role: 'assistant',
        text: `Salutations! Inspector Clawey here, surveying the Atelier at \`${routeName}\`. Have you discovered an anomaly, or do you have reflections on Tudor's learning ergonomics today?`,
        timestamp: Date.now(),
        quickChips: [
          'Visual / touch glitch',
          'ADHD pacing / timer',
          'Question wording',
          'Loved this station',
        ],
      },
    ];
  }

  private async sendMessage(text: string, image?: string): Promise<void> {
    const trimmed = text.trim();
    if ((!trimmed && !image) || this.isThinking) return;

    soundEngine.playTap();

    const userMsg: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      text: trimmed || (image ? 'Attached visual specimen' : ''),
      image: image || undefined,
      timestamp: Date.now(),
    };

    this.messages.push(userMsg);
    this.pendingImage = null;
    this.isThinking = true;
    this.render();
    this.scrollToBottom();

    try {
      const response = await FeedbackAgentClient.requestFeedbackResponse(
        this.messages,
        this.telemetry
      );

      this.isThinking = false;

      if (response.extractedCategory) {
        this.activeCategory = response.extractedCategory;
      }
      if (response.extractedSeverity) {
        this.activeSeverity = response.extractedSeverity;
      }
      if (response.suggestedTitle) {
        this.suggestedTitle = response.suggestedTitle;
      }
      if (response.suggestedAction) {
        this.suggestedAction = response.suggestedAction;
      }

      const assistantMsg: ChatMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        text: response.reply,
        timestamp: Date.now(),
        quickChips: response.quickChips,
        source: response.source,
      };

      this.messages.push(assistantMsg);
      this.quickChips = response.quickChips || [];

      // Auto-save entry progress
      this.persistCurrentEntry(false);
      this.render();
      this.scrollToBottom();
      soundEngine.playTap();
    } catch {
      this.isThinking = false;
      this.render();
    }
  }

  private persistCurrentEntry(markSavedNotification = true): void {
    const summary = this.messages
      .filter((m) => m.role === 'user')
      .map((m) => m.text)
      .join(' — ');

    const entry: FeedbackEntry = {
      id: this.currentEntryId,
      createdAt: Date.now(),
      dateFormatted: new Date().toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      title: this.suggestedTitle,
      summary: summary || 'Observation logged via Inspector Clawey',
      category: this.activeCategory,
      severity: this.activeSeverity,
      status: 'open',
      transcript: [...this.messages],
      telemetry: this.telemetry,
      suggestedAction: this.suggestedAction,
    };

    FeedbackStorage.saveEntry(entry);
    this.isSaved = true;
    this.onEntrySaved?.(entry);

    if (markSavedNotification) {
      this.showToast('✅ Specimen report safely logged to Parent Deck!');
    }
  }

  private copyAsMarkdown(): void {
    const summary = this.messages
      .filter((m) => m.role === 'user')
      .map((m) => m.text)
      .join(' — ');

    const entry: FeedbackEntry = {
      id: this.currentEntryId,
      createdAt: Date.now(),
      dateFormatted: new Date().toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
      title: this.suggestedTitle,
      summary: summary || 'Feedback observation',
      category: this.activeCategory,
      severity: this.activeSeverity,
      status: 'open',
      transcript: [...this.messages],
      telemetry: this.telemetry,
      suggestedAction: this.suggestedAction,
    };

    const md = FeedbackStorage.formatAsGitHubIssue(entry);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(md).then(() => {
        this.showToast('📋 GitHub Issue Markdown copied to clipboard!');
      });
    } else {
      this.showToast('Unable to access clipboard directly.');
    }
  }

  private showToast(msg: string): void {
    const toast = this.container.querySelector<HTMLElement>('#feedback-drawer-toast');
    if (toast) {
      toast.textContent = msg;
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(10px)';
      }, 3000);
    }
  }

  private scrollToBottom(): void {
    const chatBody = this.container.querySelector<HTMLElement>('#feedback-chat-body');
    if (chatBody) {
      chatBody.scrollTop = chatBody.scrollHeight;
    }
  }

  render(): HTMLElement {
    if (!this.isOpen) {
      this.container.innerHTML = '';
      return this.container;
    }

    const categoryIcons: Record<FeedbackCategory, string> = {
      bug: '🐛 Bug',
      adhd_friction: '🧠 ADHD Friction',
      curriculum: '📚 Curriculum',
      feature: '✨ Feature',
      praise: '⭐ Praise',
    };

    this.container.innerHTML = `
      <!-- Backdrop Overlay -->
      <div id="feedback-drawer-backdrop" style="
        position: fixed;
        inset: 0;
        background: rgba(7, 20, 36, 0.55);
        backdrop-filter: blur(5px);
        -webkit-backdrop-filter: blur(5px);
        z-index: 998;
        animation: fadeInBackdrop 0.2s ease;
      "></div>

      <!-- Slide-in Drawer Container -->
      <aside id="feedback-drawer-aside" style="
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        max-width: 460px;
        background: linear-gradient(180deg, #0A192F 0%, #061121 100%);
        border-left: 2px solid ${tokens.color.gildedBrass};
        box-shadow: -8px 0 32px rgba(0, 0, 0, 0.6);
        z-index: 999;
        display: flex;
        flex-direction: column;
        color: #F7FAFC;
        font-family: var(--font-body, system-ui);
        animation: slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1);
      ">
        <!-- Toast Notification -->
        <div id="feedback-drawer-toast" style="
          position: absolute;
          top: 16px;
          left: 16px;
          right: 16px;
          padding: 10px 16px;
          background: #0284C7;
          color: white;
          font-weight: 700;
          font-size: 0.85rem;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          z-index: 1000;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.25s ease;
          pointer-events: none;
          text-align: center;
        "></div>

        <!-- Header -->
        <header style="
          padding: 16px 20px;
          border-bottom: 1.5px solid rgba(197, 160, 89, 0.35);
          background: rgba(10, 25, 47, 0.85);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        ">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              width: 44px;
              height: 44px;
              border-radius: 50%;
              border: 2px solid ${tokens.color.gildedBrass};
              background: radial-gradient(circle, #1A365D 0%, #0A192F 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1.4rem;
              box-shadow: 0 0 10px rgba(197, 160, 89, 0.4);
            ">
              🦞
            </div>
            <div>
              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                <h3 style="margin: 0; font-family: var(--font-heading); font-size: 1.15rem; color: #FFFDF9; font-weight: 800;">
                  Inspector Clawey
                </h3>
                ${
                  this.aiLive
                    ? `<span style="
                        font-size: 0.68rem;
                        padding: 2px 8px;
                        border-radius: 999px;
                        background: rgba(16, 185, 129, 0.2);
                        color: #34D399;
                        border: 1px solid #10B981;
                        font-weight: 800;
                        text-transform: uppercase;
                      ">
                        ⚡ Gemini 3.8 Flash Live
                      </span>`
                    : `<span style="
                        font-size: 0.68rem;
                        padding: 2px 8px;
                        border-radius: 999px;
                        background: rgba(229, 169, 60, 0.2);
                        color: #FBBF24;
                        border: 1px solid #F59E0B;
                        font-weight: 800;
                        text-transform: uppercase;
                      " title="Using local Victorian Socratic heuristic engine. Set GEMINI_API_KEY in .env for live Gemini AI">
                        📴 Heuristic Socratic Mode
                      </span>`
                }
              </div>
              <div style="font-size: 0.75rem; color: #94A3B8; margin-top: 2px;">
                Victorian Deep-Sea Field Inspector &amp; Ergonomics Log
              </div>
            </div>
          </div>

          <button id="feedback-drawer-close-btn" class="touch-target" style="
            background: none;
            border: none;
            color: #94A3B8;
            font-size: 1.5rem;
            cursor: pointer;
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
          " aria-label="Close Feedback Drawer">
            &times;
          </button>
        </header>

        <!-- Telemetry Ribbon -->
        <div style="
          padding: 8px 18px;
          background: rgba(197, 160, 89, 0.1);
          border-bottom: 1px solid rgba(197, 160, 89, 0.2);
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.75rem;
          color: #CBD5E1;
          flex-shrink: 0;
        ">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span>📍 <strong>Route:</strong> <code>${this.telemetry.route}</code></span>
            <span>·</span>
            <span>${this.telemetry.activeProfile === 'tester' ? '🧪 Tester Sandbox' : '⭐ Year 4 Tudor'}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="
              padding: 2px 6px;
              border-radius: 4px;
              background: rgba(2, 132, 199, 0.2);
              color: #7DD3FC;
              font-weight: 700;
            ">
              ${categoryIcons[this.activeCategory]}
            </span>
            <span style="
              padding: 2px 6px;
              border-radius: 4px;
              background: rgba(220, 38, 38, 0.2);
              color: #FCA5A5;
              font-weight: 700;
              text-transform: uppercase;
            ">
              ${this.activeSeverity}
            </span>
          </div>
        </div>

        ${
          !this.aiLive
            ? `
          <div style="
            padding: 8px 16px;
            background: rgba(245, 158, 11, 0.1);
            border-bottom: 1px solid rgba(245, 158, 11, 0.25);
            font-size: 0.74rem;
            color: #FCD34D;
            display: flex;
            align-items: center;
            gap: 8px;
            line-height: 1.4;
          ">
            <span>ℹ️</span>
            <span><strong>AI Status:</strong> Live Gemini reasoning is idle because <code>GEMINI_API_KEY</code> is not set in <code>.env</code>. Currently responding via offline Socratic heuristic rules.</span>
          </div>
        `
            : ''
        }

        <!-- Chat Messages Body -->
        <div id="feedback-chat-body" style="
          flex: 1;
          overflow-y: auto;
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        ">
          ${this.messages
            .map((msg) => {
              const isAssistant = msg.role === 'assistant';
              return `
                <div style="
                  display: flex;
                  flex-direction: column;
                  align-items: ${isAssistant ? 'flex-start' : 'flex-end'};
                  gap: 4px;
                ">
                  <div style="
                    font-size: 0.7rem;
                    color: #94A3B8;
                    margin-left: ${isAssistant ? '4px' : '0'};
                    margin-right: ${isAssistant ? '0' : '4px'};
                    display: flex;
                    align-items: center;
                    gap: 6px;
                  ">
                    <span>${isAssistant ? '🦞 Inspector Clawey' : 'You'}</span>
                    ${
                      isAssistant && msg.source === 'gemini'
                        ? `<span style="font-size: 0.65rem; color: #34D399; font-weight: 700;">[⚡ Gemini 3.8 Flash]</span>`
                        : isAssistant
                        ? `<span style="font-size: 0.65rem; color: #FBBF24; font-weight: 600;">[📴 Heuristic]</span>`
                        : ''
                    }
                  </div>
                  <div style="
                    max-width: 85%;
                    padding: 12px 16px;
                    border-radius: ${isAssistant ? '16px 16px 16px 4px' : '16px 16px 4px 16px'};
                    background: ${
                      isAssistant
                        ? 'linear-gradient(135deg, rgba(26, 54, 93, 0.9) 0%, rgba(15, 33, 64, 0.95) 100%)'
                        : 'linear-gradient(135deg, #FFFDF9 0%, #EFE5CE 100%)'
                    };
                    color: ${isAssistant ? '#F8FAFC' : '#1A365D'};
                    border: 1.5px solid ${isAssistant ? tokens.color.gildedBrass : '#D4C5A9'};
                    font-size: 0.92rem;
                    line-height: 1.5;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.25);
                    word-break: break-word;
                  ">
                    <div>${msg.text}</div>
                    ${
                      msg.image
                        ? `
                      <div style="margin-top: 8px;">
                        <a href="${msg.image}" target="_blank" rel="noopener noreferrer" style="display: block; text-decoration: none;">
                          <img
                            src="${msg.image}"
                            alt="Visual specimen"
                            class="feedback-message-image"
                            style="
                              max-width: 100%;
                              max-height: 220px;
                              border-radius: 8px;
                              border: 1.5px solid ${isAssistant ? 'rgba(197, 160, 89, 0.5)' : '#B8A47E'};
                              display: block;
                              object-fit: contain;
                              background: rgba(0,0,0,0.2);
                            "
                          />
                          <span style="display: block; font-size: 0.72rem; color: ${isAssistant ? '#94A3B8' : '#786C55'}; margin-top: 4px; font-style: italic;">
                            🔍 Click to open full image
                          </span>
                        </a>
                      </div>
                    `
                        : ''
                    }
                  </div>
                </div>
              `;
            })
            .join('')}

          ${
            this.isThinking
              ? `
            <div style="display: flex; align-items: center; gap: 8px; color: #94A3B8; font-size: 0.85rem; padding: 6px;">
              <span style="display: inline-block; animation: pulse 1s infinite;">🔍</span>
              <span>Inspector Clawey is reviewing the Atelier evidence...</span>
            </div>
          `
              : ''
          }
        </div>

        <!-- Quick Reply Chips Stack (Vertical Stack to prevent overlap) -->
        ${
          this.quickChips && this.quickChips.length > 0
            ? `
        <div id="feedback-quick-chips-container" style="
          padding: 10px 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex-shrink: 0;
          background: rgba(10, 25, 47, 0.65);
          border-top: 1.5px solid rgba(197, 160, 89, 0.25);
          max-height: 190px;
          overflow-y: auto;
        ">
          <div style="font-size: 0.72rem; color: #CBD5E1; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; margin-bottom: 2px; display: flex; align-items: center; gap: 6px;">
            <span>💡 Suggested Responses:</span>
          </div>
          ${this.quickChips
            .map(
              (chip) => `
            <button class="feedback-quick-chip touch-target" data-chip="${chip}" style="
              width: 100%;
              text-align: left;
              padding: 9px 14px;
              border-radius: 8px;
              background: linear-gradient(135deg, rgba(26, 54, 93, 0.75) 0%, rgba(15, 33, 64, 0.85) 100%);
              border: 1.5px solid rgba(197, 160, 89, 0.45);
              color: #FFFDF9;
              font-size: 0.86rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.15s ease;
              display: flex;
              align-items: center;
              justify-content: space-between;
              box-shadow: 0 2px 6px rgba(0,0,0,0.2);
              white-space: normal;
              word-break: break-word;
            ">
              <span>${chip}</span>
              <span style="opacity: 0.65; font-size: 0.8rem; margin-left: 8px; flex-shrink: 0;">➔</span>
            </button>
          `
            )
            .join('')}
        </div>
        `
            : ''
        }

        <!-- Action / Export Footer -->
        <footer style="
          padding: 14px 18px;
          border-top: 1.5px solid rgba(197, 160, 89, 0.3);
          background: rgba(10, 25, 47, 0.95);
          display: flex;
          flex-direction: column;
          gap: 10px;
          flex-shrink: 0;
        ">
          <!-- Attached Image Preview Bar -->
          ${
            this.pendingImage
              ? `
            <div id="feedback-image-preview-bar" style="
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 8px 12px;
              background: rgba(197, 160, 89, 0.15);
              border: 1.5px solid rgba(197, 160, 89, 0.4);
              border-radius: 10px;
            ">
              <img
                src="${this.pendingImage.dataUrl}"
                alt="Pasted specimen preview"
                id="feedback-pending-img-preview"
                style="
                  width: 44px;
                  height: 44px;
                  object-fit: cover;
                  border-radius: 6px;
                  border: 1.5px solid rgba(197, 160, 89, 0.6);
                  background: #000;
                "
              />
              <div style="flex: 1; min-width: 0;">
                <div style="font-size: 0.82rem; font-weight: 700; color: #FFFDF9; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  🖼️ ${this.pendingImage.name || 'Pasted Image'}
                </div>
                <div style="font-size: 0.7rem; color: #94A3B8;">
                  Visual specimen attached · Will send with message
                </div>
              </div>
              <button
                id="feedback-remove-image-btn"
                type="button"
                style="
                  background: rgba(239, 68, 68, 0.2);
                  border: 1px solid rgba(239, 68, 68, 0.5);
                  color: #FCA5A5;
                  border-radius: 6px;
                  width: 28px;
                  height: 28px;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-weight: bold;
                "
                title="Remove attached image"
              >
                ✕
              </button>
            </div>
          `
              : ''
          }

          <!-- Text Input Form with Attachment & Paste Support -->
          <form id="feedback-chat-form" style="display: flex; gap: 8px; align-items: center;">
            <input
              type="file"
              id="feedback-image-file-input"
              accept="image/*"
              style="display: none;"
            />
            <button
              id="feedback-attach-file-btn"
              type="button"
              class="touch-target"
              title="Attach visual specimen or paste screenshot (Ctrl+V)"
              style="
                padding: 10px;
                background: rgba(255, 253, 249, 0.08);
                border: 1.5px solid rgba(197, 160, 89, 0.4);
                border-radius: 10px;
                color: #F8FAFC;
                cursor: pointer;
                font-size: 1.1rem;
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 44px;
                height: 44px;
              "
            >
              📎
            </button>
            <input
              id="feedback-chat-input"
              type="text"
              placeholder="Tell Clawey what you noticed or paste image (Ctrl+V)..."
              autocomplete="off"
              style="
                flex: 1;
                padding: 12px 14px;
                background: rgba(255, 253, 249, 0.08);
                border: 1.5px solid rgba(197, 160, 89, 0.4);
                border-radius: 10px;
                color: #FFFDF9;
                font-size: 0.92rem;
                outline: none;
              "
            />
            <button type="submit" class="touch-target btn-brass" style="
              padding: 10px 18px;
              border-radius: 10px;
              font-weight: 700;
              font-size: 0.9rem;
              cursor: pointer;
              min-width: 68px;
              height: 44px;
            ">
              Send
            </button>
          </form>

          <!-- Report Actions Toolbar -->
          <div style="display: flex; gap: 8px; justify-content: space-between; align-items: center;">
            <button id="feedback-save-btn" class="touch-target" style="
              flex: 1;
              padding: 8px 12px;
              background: rgba(16, 185, 129, 0.15);
              border: 1.5px solid #10B981;
              color: #A7F3D0;
              border-radius: 8px;
              font-size: 0.82rem;
              font-weight: 700;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
            ">
              💾 Save to Logbook
            </button>

            <button id="feedback-copy-md-btn" class="touch-target" style="
              flex: 1;
              padding: 8px 12px;
              background: rgba(2, 132, 199, 0.15);
              border: 1.5px solid #0284C7;
              color: #BAE6FD;
              border-radius: 8px;
              font-size: 0.82rem;
              font-weight: 700;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
            ">
              📋 Copy GitHub Issue
            </button>

            <button id="feedback-reset-btn" class="touch-target" style="
              padding: 8px 12px;
              background: rgba(148, 163, 184, 0.1);
              border: 1px solid rgba(148, 163, 184, 0.3);
              color: #CBD5E1;
              border-radius: 8px;
              font-size: 0.82rem;
              cursor: pointer;
            " title="Start new observation">
              🔄
            </button>
          </div>
        </footer>
      </aside>
    `;

    this.attachEventListeners();
    return this.container;
  }

  private attachEventListeners(): void {
    // Backdrop click to close
    const backdrop = this.container.querySelector('#feedback-drawer-backdrop');
    backdrop?.addEventListener('click', () => this.close());

    // Close button
    const closeBtn = this.container.querySelector('#feedback-drawer-close-btn');
    closeBtn?.addEventListener('click', () => this.close());

    // Clipboard image paste event listener on both input and drawer
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item && item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            this.handleImageFile(file);
            break;
          }
        }
      }
    };

    const input = this.container.querySelector<HTMLInputElement>('#feedback-chat-input');
    input?.addEventListener('paste', handlePaste as EventListener);

    const aside = this.container.querySelector('#feedback-drawer-aside');
    aside?.addEventListener('paste', handlePaste as EventListener);

    // Attachment file input & button
    const fileInput = this.container.querySelector<HTMLInputElement>('#feedback-image-file-input');
    const attachBtn = this.container.querySelector<HTMLButtonElement>('#feedback-attach-file-btn');
    attachBtn?.addEventListener('click', () => {
      fileInput?.click();
    });
    fileInput?.addEventListener('change', () => {
      if (fileInput.files && fileInput.files[0]) {
        this.handleImageFile(fileInput.files[0]);
        fileInput.value = '';
      }
    });

    // Remove image button
    const removeImgBtn = this.container.querySelector('#feedback-remove-image-btn');
    removeImgBtn?.addEventListener('click', () => {
      this.pendingImage = null;
      this.render();
    });

    // Chat submit form
    const form = this.container.querySelector<HTMLFormElement>('#feedback-chat-form');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const val = input ? input.value : '';
      if (val || this.pendingImage) {
        if (input) input.value = '';
        const imgToSend = this.pendingImage?.dataUrl;
        this.pendingImage = null;
        this.sendMessage(val, imgToSend);
      }
    });

    // Quick chips
    const chipBtns = this.container.querySelectorAll<HTMLButtonElement>('.feedback-quick-chip');
    chipBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const chip = btn.getAttribute('data-chip');
        if (chip) {
          const imgToSend = this.pendingImage?.dataUrl;
          this.pendingImage = null;
          this.sendMessage(chip, imgToSend);
        }
      });
    });

    // Save button
    const saveBtn = this.container.querySelector('#feedback-save-btn');
    saveBtn?.addEventListener('click', () => {
      this.persistCurrentEntry(true);
      soundEngine.playTap();
    });

    // Copy GitHub Issue Markdown button
    const copyBtn = this.container.querySelector('#feedback-copy-md-btn');
    copyBtn?.addEventListener('click', () => {
      this.copyAsMarkdown();
      soundEngine.playTap();
    });

    // Reset button
    const resetBtn = this.container.querySelector('#feedback-reset-btn');
    resetBtn?.addEventListener('click', () => {
      this.pendingImage = null;
      this.initSession();
      this.render();
      soundEngine.playTap();
    });
  }
}
